import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Eye, EyeOff, Check, AlertTriangle, Lock } from 'lucide-react';
import * as auth from '../services/auth';
import { validatePassword, getPasswordRequirements, getPasswordStrength, PASSWORD_STRENGTH_LABELS, PASSWORD_STRENGTH_COLORS } from '../utils/passwordPolicy';

export default function InviteRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please use the link from your invitation email.');
      setLoading(false);
      return;
    }

    const validate = async () => {
      const result = await auth.validateInviteToken(token);
      if (result.ok) {
        setInviteData(result.data);
      } else {
        setError(result.error || 'Invalid or expired invitation');
      }
      setLoading(false);
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const pwResult = validatePassword(password);
    if (!pwResult.valid) {
      setSubmitError(pwResult.errors[0]);
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const result = await auth.registerWithInvite(token, { password });
    if (result.ok) {
      setSuccess(true);
    } else {
      setSubmitError(result.error || 'Registration failed');
    }
    setSubmitting(false);
  };

  const strength = getPasswordStrength(password);

  // ─── Shared styles ────────────────────────────────────────────────────────
  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fafaf9 0%, #f5f5f4 50%, #e3f4f1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    padding: 24,
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: 460,
    padding: 40,
    border: '1px solid #e7e5e4',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #e4e4e7',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#52525b',
    marginBottom: 6,
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e3f4f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Shield size={24} color="#0d9488" />
            </div>
            <p style={{ fontSize: 14, color: '#71717a', margin: 0 }}>Validating your invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Error (invalid/expired token) ────────────────────────────────────────
  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fef2f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <AlertTriangle size={24} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#18181b', margin: '0 0 8px 0' }}>Invalid Invitation</h2>
            <p style={{ fontSize: 14, color: '#71717a', margin: '0 0 24px 0' }}>{error}</p>
            <button onClick={() => navigate('/login')} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: '#18181b', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Success ──────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e3f4f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Check size={28} color="#0d9488" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#18181b', margin: '0 0 8px 0' }}>Registration Complete</h2>
            <p style={{ fontSize: 14, color: '#71717a', margin: '0 0 8px 0' }}>
              Your account has been created successfully.
            </p>
            <p style={{ fontSize: 13, color: '#a8a29e', margin: '0 0 24px 0' }}>
              You can now sign in with <strong style={{ color: '#18181b' }}>{inviteData?.email}</strong> and your new password.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 32px', borderRadius: 8, border: 'none',
              background: '#0d9488', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Registration Form ────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <img src="/brand/mark-dark.png" alt="Prove7 Control Vector Platform" style={{ width: 48, height: 'auto' }} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#18181b', margin: '0 0 6px 0' }}>
            Complete Your Registration
          </h1>
          <p style={{ fontSize: 14, color: '#71717a', margin: 0 }}>
            You've been invited to join Prove7 Control Vector
          </p>
        </div>

        {/* Pre-filled info */}
        <div style={{
          background: '#f5f5f4', borderRadius: 8, padding: 16, marginBottom: 24,
          border: '1px solid #e7e5e4',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: '#a8a29e', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Name</div>
              <div style={{ fontSize: 13, color: '#18181b', fontWeight: 600 }}>
                {inviteData?.firstName} {inviteData?.lastName}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a8a29e', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Role</div>
              <div style={{ fontSize: 13, color: '#18181b', fontWeight: 600 }}>{inviteData?.role}</div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: '#a8a29e', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Login Email</div>
            <div style={{ fontSize: 13, color: '#18181b', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
              {inviteData?.email}
            </div>
          </div>
        </div>

        {/* Password form */}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>
                <Lock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Create Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a8a29e', padding: 4 }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 3, flex: 1 }}>
                    {[1,2,3,4].map((i) => (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: strength >= i ? PASSWORD_STRENGTH_COLORS[strength] : '#e4e4e7',
                        transition: 'background 0.2s',
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: PASSWORD_STRENGTH_COLORS[strength], fontWeight: 600, minWidth: 44 }}>
                    {PASSWORD_STRENGTH_LABELS[strength]}
                  </span>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#a8a29e', marginTop: 4 }}>
                {getPasswordRequirements()}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingRight: 40,
                    borderColor: confirmPassword && confirmPassword !== password ? '#ef4444' : confirmPassword && confirmPassword === password ? '#0d9488' : '#e4e4e7',
                  }}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a8a29e', padding: 4 }}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Passwords do not match</div>
              )}
              {confirmPassword && confirmPassword === password && (
                <div style={{ fontSize: 11, color: '#0d9488', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Check size={12} /> Passwords match
                </div>
              )}
            </div>

            {submitError && (
              <div style={{
                padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 8, fontSize: 13, color: '#991b1b',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertTriangle size={14} />
                {submitError}
              </div>
            )}

            <button type="submit" disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
              style={{
                padding: '12px 24px', borderRadius: 8, border: 'none',
                background: submitting || !password || !confirmPassword || password !== confirmPassword ? '#d6d3d1' : '#0d9488',
                color: '#ffffff', fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer',
                transition: 'background 0.2s', marginTop: 8,
              }}>
              {submitting ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </div>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', color: '#a8a29e', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            Already have an account? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
